'use client'

import { useState, useMemo } from 'react'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'

import { normalizeForSearch } from '@/utils/strings'
import { ChevronDown } from 'lucide-react'
import { getTokenImageUrl, NormalizedTokenInfo } from '@/domain/euler'
import { Address } from 'viem'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { truncateWithEllipsis } from '@/domain/common/format'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'

export interface TokenOption {
  underlying: Address
  symbol: string
  name?: string
  logoURI?: string
  tokenInfo?: NormalizedTokenInfo
  /** User's balance of this token */
  balance?: string
  /** Price per token in USD */
  priceUsd?: number
  /** Liquidity hint for sorting borrow options */
  liquidityUsdHint?: number
}

interface TokenSelectorDialogProps {
  options: TokenOption[]
  selectedUnderlying?: Address
  onSelect: (underlying: Address) => void
  title: string
  placeholder?: string
  disabled?: boolean
  /** Sort by USD value (balance * price) descending */
  sortByBalance?: boolean
  /** Sort by liquidity descending */
  sortByLiquidity?: boolean
  /** Show balance and USD value */
  showBalance?: boolean
  /** Show liquidity */
  showLiquidity?: boolean
}

export function TokenSelectorDialog({
  options,
  selectedUnderlying,
  onSelect,
  title,
  placeholder = 'Select token',
  disabled = false,
  sortByBalance = false,
  sortByLiquidity = false,
  showBalance = false,
  showLiquidity = false,
}: TokenSelectorDialogProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selectedOption = useMemo(
    () => options.find((opt) => opt.underlying.toLowerCase() === selectedUnderlying?.toLowerCase()),
    [options, selectedUnderlying],
  )

  const sortedOptions = useMemo(() => {
    let filtered = options
    if (search) {
      const query = normalizeForSearch(search)
      filtered = filtered.filter(
        (opt) => normalizeForSearch(opt.symbol).includes(query) || normalizeForSearch(opt.name || '').includes(query),
      )
    }

    const sorted = [...filtered]
    if (sortByBalance) {
      sorted.sort((a, b) => {
        const aUsd = Number(a.balance || 0) * (a.priceUsd || 0)
        const bUsd = Number(b.balance || 0) * (b.priceUsd || 0)
        return bUsd - aUsd
      })
    } else if (sortByLiquidity) {
      sorted.sort((a, b) => (b.liquidityUsdHint || 0) - (a.liquidityUsdHint || 0))
    }
    return sorted
  }, [options, search, sortByBalance, sortByLiquidity])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        className={cn(
          'flex h-[72px] w-[160px] shrink-0 items-center justify-between rounded-l-lg border-border border-r bg-transparent px-4 hover:bg-white/5',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        disabled={disabled}
      >
        {selectedOption ? (
          <div className="flex items-center gap-2">
            <TokenImage
              src={getTokenImageUrl(selectedOption.tokenInfo, selectedOption.symbol)}
              alt={selectedOption.symbol}
              width={28}
              height={28}
              className="rounded-full"
              tokenAddress={selectedOption.underlying}
            />
            <span className="font-semibold text-foreground text-lg">
              {truncateWithEllipsis(selectedOption.symbol, 6)}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </DialogTrigger>
      <DialogContent
        className="border border-border bg-background pt-6 md:min-w-0 md:max-w-md md:pb-2"
        aria-describedby={undefined}
        closeClassName="top-6"
      >
        <DialogTitle>
          <Typography variant="p" className="font-bold text-lg" gradient>
            {title}
          </Typography>
        </DialogTitle>
        <div className="mt-3 mb-4">
          <SearchInput value={search} placeholder="Search tokens..." onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="no-scrollbar space-y-1 overflow-y-auto md:max-h-80">
          {sortedOptions.length === 0 ? (
            <div className="py-8 text-center">
              <Typography variant="prompt" className="text-muted-foreground">
                {search ? 'No tokens found' : 'No tokens available'}
              </Typography>
            </div>
          ) : (
            sortedOptions.map((opt) => {
              const balanceUsd = Number(opt.balance || 0) * (opt.priceUsd || 0)
              const isSelected = opt.underlying.toLowerCase() === selectedUnderlying?.toLowerCase()
              return (
                <button
                  key={String(opt.underlying)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg p-3 transition-all active:scale-[0.99] hover:bg-white/5',
                    isSelected && 'bg-white/5',
                  )}
                  onClick={() => {
                    onSelect(opt.underlying)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  <div className="flex items-center gap-3">
                    <TokenImage
                      src={getTokenImageUrl(opt.tokenInfo, opt.symbol)}
                      alt={opt.symbol}
                      width={36}
                      height={36}
                      className="rounded-full"
                      tokenAddress={opt.underlying}
                    />
                    <div className="flex flex-col items-start">
                      <Typography className="font-semibold">{opt.symbol}</Typography>
                      {opt.name && (
                        <Typography variant="prompt" className="text-left text-xs">
                          {opt.name}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    {showBalance && opt.balance && Number(opt.balance) > 0 && (
                      <>
                        <Typography className="font-medium">
                          {Number(opt.balance) >= 1000
                            ? Number(opt.balance).toLocaleString(undefined, { maximumFractionDigits: 2 })
                            : Number(opt.balance).toFixed(Number(opt.balance) < 1 ? 4 : 2)}
                        </Typography>
                        {balanceUsd > 0 && (
                          <Typography variant="prompt" className="text-xs">
                            {USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(balanceUsd), { compact: true })}
                          </Typography>
                        )}
                      </>
                    )}
                    {showLiquidity && opt.liquidityUsdHint !== undefined && opt.liquidityUsdHint > 0 && (
                      <Typography variant="prompt" className="text-xs">
                        {USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(opt.liquidityUsdHint), { compact: true })} liq
                      </Typography>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
